// ── Studio API Layer ─────────────────────────────────────────────────────────

function normalizeProxyBase(value) {
  return String(value || '').replace(/\/$/, '')
}

export function resolveProxyBase(env = import.meta.env) {
  return normalizeProxyBase(
    env.PROD
      ? env.VITE_PROXY_URL || ''
      : env.VITE_PROXY_URL || 'http://localhost:4000'
  )
}

export const PROXY = resolveProxyBase()

const CSRF_HEADER = 'x-csrf-token'
let cachedCsrfToken = null

function readCookie(name) {
  if (typeof document === 'undefined') return ''
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : ''
}

function captureCsrfToken(headers) {
  if (!headers || typeof headers.get !== 'function') return
  const next = headers.get('X-CSRF-Token') || headers.get('x-csrf-token')
  if (next) cachedCsrfToken = next
}

function getCsrfTokenSync() {
  if (cachedCsrfToken) return cachedCsrfToken
  const cookieToken = readCookie('ignazio_studio_csrf')
  if (cookieToken) cachedCsrfToken = cookieToken
  return cachedCsrfToken
}

export async function ensureCsrfToken() {
  const existing = getCsrfTokenSync()
  if (existing) return existing
  try {
    const res = await fetch(`${PROXY}/api/csrf-token`, {
      credentials: 'include',
    })
    captureCsrfToken(res.headers)
    if (res.ok) {
      const body = await res.json().catch(() => ({}))
      if (body && typeof body.token === 'string') {
        cachedCsrfToken = body.token
      }
    }
  } catch {
    // Fall through; mutations will surface CSRF errors normally.
  }
  return cachedCsrfToken
}

function isMutation(method) {
  const upper = String(method || 'GET').toUpperCase()
  return upper !== 'GET' && upper !== 'HEAD' && upper !== 'OPTIONS'
}

export async function apiJson(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  }
  if (isMutation(options.method)) {
    const token = await ensureCsrfToken()
    if (token && !headers[CSRF_HEADER] && !headers['X-CSRF-Token']) {
      headers[CSRF_HEADER] = token
    }
  }
  const res = await fetch(`${PROXY}${path}`, {
    credentials: 'include',
    ...options,
    headers,
  })
  captureCsrfToken(res.headers)
  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.error) {
    const error = new Error(
      typeof data.error === 'string' ? data.error : 'Request failed'
    )
    error.code = data.code || 'REQUEST_FAILED'
    error.status = res.status
    error.verification = data.verification || null
    error.bootstrap = data.bootstrap || null
    error.generation = data.generation || null
    error.text = typeof data.text === 'string' ? data.text : ''
    if (typeof data.retryAfterMs === 'number') {
      error.retryAfterMs = data.retryAfterMs
    }
    throw error
  }
  return data
}

export async function apiCostUsage({ windowMs, recent } = {}) {
  const params = {}
  if (windowMs) params.windowMs = windowMs
  if (recent) params.recent = 'true'
  return apiJson(`/api/cost/usage${buildQuery(params)}`)
}

// ── Strategist (self-optimizing intelligence layer) ──────────────────────────
export async function apiStrategistLatestReview() {
  return apiJson('/api/strategist/reviews/latest')
}

export async function apiStrategistRunNow({ dryRun = false } = {}) {
  return apiJson('/api/strategist/run-now', {
    method: 'POST',
    body: JSON.stringify({ dryRun }),
  })
}

export async function apiStrategistApplyFinding(reviewId, findingId) {
  return apiJson(
    `/api/strategist/reviews/${encodeURIComponent(reviewId)}/apply-finding`,
    { method: 'POST', body: JSON.stringify({ findingId }) }
  )
}

export async function apiStrategistDismissFinding(reviewId, findingId) {
  return apiJson(
    `/api/strategist/reviews/${encodeURIComponent(reviewId)}/dismiss-finding`,
    { method: 'POST', body: JSON.stringify({ findingId }) }
  )
}

export async function apiStrategistLogEngagement(payload) {
  return apiJson('/api/strategist/log-engagement', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

export async function apiAuthStatus() {
  return apiJson('/api/auth/status')
}

export async function apiLogin(password) {
  return apiJson('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  })
}

export async function apiLogout() {
  return apiJson('/api/auth/logout', {
    method: 'POST',
  })
}

export function buildQuery(params) {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return
    search.set(key, String(value))
  })
  const query = search.toString()
  return query ? `?${query}` : ''
}

export async function apiGenerate(
  pillarId,
  contentType,
  topic,
  ctx,
  truthLinks = {},
  assetIds = [],
  options = {}
) {
  return apiJson('/api/claude', {
    method: 'POST',
    body: JSON.stringify(
      buildGeneratePayload(
        pillarId,
        contentType,
        topic,
        ctx,
        truthLinks,
        assetIds,
        options
      )
    ),
  })
}

export function buildGeneratePayload(
  pillarId,
  contentType,
  topic,
  ctx,
  truthLinks = {},
  assetIds = [],
  options = {}
) {
  return {
    pillarId,
    contentType,
    topic,
    ctx,
    projectIds: truthLinks.projectIds || [],
    positionIds: truthLinks.positionIds || [],
    assetIds,
    strictVoice: options.strictVoice ?? false,
    githubAccessToken: options.githubAccessToken || '',
    githubRepos: options.githubRepos || [],
    authorityContext: options.authorityContext || null,
  }
}

export async function apiLinkedIn(
  postId,
  content,
  accessToken,
  authorId,
  contentType
) {
  return apiJson('/api/linkedin/post', {
    method: 'POST',
    body: JSON.stringify({
      postId,
      content,
      accessToken,
      authorId,
      contentType,
    }),
  })
}

export async function apiTwitter(postId, content, accessToken, contentType) {
  return apiJson('/api/twitter/post', {
    method: 'POST',
    body: JSON.stringify({ postId, content, accessToken, contentType }),
  })
}

export async function apiCarousel(
  pillarId,
  topic,
  ctx,
  truthLinks = {},
  assetIds = [],
  options = {}
) {
  const data = await apiJson('/api/claude/carousel', {
    method: 'POST',
    body: JSON.stringify(
      buildCarouselPayload(pillarId, topic, ctx, truthLinks, assetIds, options)
    ),
  })
  return data.slides
}

export function buildCarouselPayload(
  pillarId,
  topic,
  ctx,
  truthLinks = {},
  assetIds = [],
  options = {}
) {
  return {
    pillarId,
    topic,
    ctx,
    projectIds: truthLinks.projectIds || [],
    positionIds: truthLinks.positionIds || [],
    assetIds,
    githubAccessToken: options.githubAccessToken || '',
    githubRepos: options.githubRepos || [],
    authorityContext: options.authorityContext || null,
  }
}

export async function apiDiagram(topic, type, visualNote, assetIds = []) {
  return apiJson('/api/claude/diagram', {
    method: 'POST',
    body: JSON.stringify({ topic, type, visualNote, assetIds }),
  })
}

export async function apiReport(title, briefIds, assetIds = []) {
  return apiJson('/api/report/generate', {
    method: 'POST',
    body: JSON.stringify({ title, briefIds, assetIds }),
  })
}

export async function apiBootstrapStudio() {
  return apiJson('/api/studio/bootstrap')
}

// Perf 2026-06-10 — archived posts are loaded OFF the blocking bootstrap
// (prod archive is ~14MB / thousands of rows). Fetched here as a deferred
// post-paint background fill, on Archive-tab open, and before a backup export.
export async function apiFetchArchivedPosts() {
  const data = await apiJson('/api/posts/archived')
  return Array.isArray(data?.archivedPosts) ? data.archivedPosts : []
}

export async function apiSavePost(post) {
  const data = await apiJson('/api/posts', {
    method: 'POST',
    body: JSON.stringify({ post }),
  })
  return data.post
}

export async function apiUpdatePost(id, updates) {
  const data = await apiJson(`/api/posts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  })
  return data.post
}

export async function apiFetchDeliveryReceipts(postId) {
  return apiJson(`/api/posts/${postId}/delivery-receipts`)
}

// Substack drafts-only push (P1, 2026-05-27). Pushes a newsletter-issue
// post to Substack as a draft for operator review + manual publish.
// Returns { substackDraftId, previewUrl, action, pushedAt, post }.
// See docs/substack-integration.md.
export async function apiSubstackPushDraft(postId) {
  return apiJson('/api/substack/push-draft', {
    method: 'POST',
    body: JSON.stringify({ postId }),
  })
}

export async function apiSubstackHealth() {
  return apiJson('/api/substack/health')
}

export async function apiDeletePost(id) {
  return apiJson(`/api/posts/${id}`, { method: 'DELETE' })
}

export async function apiSaveIdea(idea) {
  const data = await apiJson('/api/ideas', {
    method: 'POST',
    body: JSON.stringify({ idea }),
  })
  return data.idea
}

export async function apiDeleteIdea(id) {
  return apiJson(`/api/ideas/${id}`, { method: 'DELETE' })
}

export async function apiUpdateIdea(id, updates) {
  const data = await apiJson(`/api/ideas/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  })
  return data.idea
}

export async function apiUpdateResearchBrief(id, updates) {
  const data = await apiJson(`/api/research/briefs/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  })
  return data.brief
}

export async function apiFetchProofAssets(filters = {}) {
  return apiJson(`/api/proof/assets${buildQuery(filters)}`)
}

export async function apiUpdateProofAsset(id, updates) {
  return apiJson(`/api/proof/assets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  })
}

export async function apiReindexProofAssets() {
  return apiJson('/api/proof/assets/reindex', { method: 'POST' })
}

export async function apiCreateTopicFromProofAsset(id) {
  return apiJson(`/api/proof/assets/${id}/topic-candidate`, {
    method: 'POST',
  })
}

export async function apiUpdateStartDate(startDate) {
  return apiJson('/api/settings/start-date', {
    method: 'POST',
    body: JSON.stringify({ startDate }),
  })
}

export async function apiFetchGithubSettings() {
  return apiJson('/api/settings/github')
}

export async function apiUpdateGithubSettings(repos, repoActivityCutoff = '') {
  return apiJson('/api/settings/github', {
    method: 'POST',
    body: JSON.stringify({ repos, repoActivityCutoff }),
  })
}

export async function apiGithubPromptContext({
  accessToken = '',
  repos = [],
  maxRepos,
  maxItemsPerRepo,
  recentDays,
  ensureFresh = true,
} = {}) {
  return apiJson('/api/github/prompt-context', {
    method: 'POST',
    body: JSON.stringify({
      accessToken,
      repos,
      maxRepos,
      maxItemsPerRepo,
      recentDays,
      ensureFresh,
    }),
  })
}

// Wave H — discover all GitHub repos the operator's token can see,
// each tagged with whether it's currently watched (in allowlist).
export async function apiDiscoverGithubRepos({
  visibility,
  accessToken = '',
  perPage,
  pageCap,
} = {}) {
  if (accessToken) {
    return apiJson('/api/github/repos/discover', {
      method: 'POST',
      body: JSON.stringify({
        accessToken,
        visibility,
        perPage,
        pageCap,
      }),
    })
  }
  const params = new URLSearchParams()
  if (visibility) params.set('visibility', visibility)
  if (perPage != null) params.set('perPage', String(perPage))
  if (pageCap != null) params.set('pageCap', String(pageCap))
  const qs = params.toString()
  return apiJson(`/api/github/repos/discover${qs ? `?${qs}` : ''}`)
}

// Wave H — toggle a single repo on/off the watch allowlist.
export async function apiSetGithubRepoWatched({ repo, watched }) {
  return apiJson('/api/github/repos/watch', {
    method: 'POST',
    body: JSON.stringify({ repo, watched: !!watched }),
  })
}

// Wave I — autonomy promotion readiness assessment.
export async function apiAutonomyPromotionReadiness({
  lookbackDays,
  minDecisions,
} = {}) {
  const params = new URLSearchParams()
  if (Number.isFinite(lookbackDays)) {
    params.set('lookbackDays', String(lookbackDays))
  }
  if (Number.isFinite(minDecisions)) {
    params.set('minDecisions', String(minDecisions))
  }
  const qs = params.toString()
  return apiJson(`/api/autonomy/promotion-readiness${qs ? `?${qs}` : ''}`)
}

// Wave I — promote/demote a content type's autonomy mode in one call.
// Wraps the existing /api/autonomy/policies/:contentType POST.
export async function apiSetContentTypeAutonomyMode({
  contentType,
  autonomyMode,
}) {
  return apiJson(`/api/autonomy/policies/${encodeURIComponent(contentType)}`, {
    method: 'POST',
    body: JSON.stringify({ autonomyMode }),
  })
}

// Wave K — propose engagement-boost rules from current insights.
// dryRun=true returns proposals without persisting; default persists
// disabled rules for operator review.
export async function apiProposeEngagementBoostRules({
  dryRun = false,
  minBiasFactor,
  minPostCount,
  minScore,
  lookbackDays,
  maxProposals,
} = {}) {
  const body = { dryRun }
  if (Number.isFinite(minBiasFactor)) body.minBiasFactor = minBiasFactor
  if (Number.isFinite(minPostCount)) body.minPostCount = minPostCount
  if (Number.isFinite(minScore)) body.minScore = minScore
  if (Number.isFinite(lookbackDays)) body.lookbackDays = lookbackDays
  if (Number.isFinite(maxProposals)) body.maxProposals = maxProposals
  return apiJson('/api/autonomy/engagement-boost-rules/propose', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function apiGithubIngest(payload) {
  return apiJson('/api/github/ingest', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiContentReview(payload) {
  return apiJson('/api/content/review', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiFetchAuthorityInbox() {
  return apiJson('/api/authority/inbox')
}

export async function apiRunAuthorityReview(payload) {
  return apiJson('/api/authority/reviews/run', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiUpdateAuthorityTopicCandidate(id, updates) {
  return apiJson(`/api/authority/topic-candidates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  })
}

export async function apiConvertAuthorityTopicCandidate(id) {
  return apiJson(`/api/authority/topic-candidates/${id}/convert`, {
    method: 'POST',
  })
}

export async function apiFetchProjects(includeArchived = false) {
  const data = await apiJson(
    `/api/truth/projects${buildQuery({ includeArchived: includeArchived || null })}`
  )
  return data.projects
}

export async function apiSaveProject(project) {
  const data = await apiJson('/api/truth/projects', {
    method: 'POST',
    body: JSON.stringify({ project }),
  })
  return data.project
}

export async function apiUpdateProject(id, updates) {
  const data = await apiJson(`/api/truth/projects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  })
  return data.project
}

export async function apiDeleteProject(id) {
  return apiJson(`/api/truth/projects/${id}`, { method: 'DELETE' })
}

export async function apiFetchPositions(includeArchived = false) {
  const data = await apiJson(
    `/api/truth/positions${buildQuery({ includeArchived: includeArchived || null })}`
  )
  return data.positions
}

export async function apiSavePosition(position) {
  const data = await apiJson('/api/truth/positions', {
    method: 'POST',
    body: JSON.stringify({ position }),
  })
  return data.position
}

export async function apiUpdatePosition(id, updates) {
  const data = await apiJson(`/api/truth/positions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  })
  return data.position
}

export async function apiDeletePosition(id) {
  return apiJson(`/api/truth/positions/${id}`, { method: 'DELETE' })
}

export async function apiFetchTruthSuggestions(status = null) {
  const data = await apiJson(
    `/api/truth/suggestions${buildQuery({ status: status || null })}`
  )
  return data.suggestions
}

export async function apiFetchTruthReviewQueue() {
  return apiJson('/api/truth/review-queue')
}

export async function apiSaveTruthSuggestion(suggestion) {
  const data = await apiJson('/api/truth/suggestions', {
    method: 'POST',
    body: JSON.stringify({ suggestion }),
  })
  return data.suggestion
}

export async function apiInferTruthSuggestions() {
  return apiJson('/api/truth/suggestions', {
    method: 'POST',
    body: JSON.stringify({ action: 'infer' }),
  })
}

export async function apiBatchResolveTruthSuggestions(decisions) {
  return apiJson('/api/truth/suggestions/batch-resolve', {
    method: 'POST',
    body: JSON.stringify({ decisions }),
  })
}

export async function apiApproveTruthSuggestion(id, decision) {
  return apiJson(`/api/truth/suggestions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ action: 'approve', decision }),
  })
}

export async function apiDismissTruthSuggestion(id) {
  return apiJson(`/api/truth/suggestions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ action: 'dismiss' }),
  })
}

export async function apiDeleteTruthSuggestion(id) {
  return apiJson(`/api/truth/suggestions/${id}`, { method: 'DELETE' })
}

export async function apiImportLocalSnapshot(snapshot) {
  return apiJson('/api/migrations/import-local-snapshot', {
    method: 'POST',
    body: JSON.stringify({ snapshot }),
  })
}

export async function apiListArtifacts(kind, limit = 20) {
  const data = await apiJson(`/api/artifacts${buildQuery({ kind, limit })}`)
  return data.artifacts
}

export async function apiFetchReports() {
  const data = await apiJson('/api/reports')
  return data.reports
}

export async function apiFetchPublishAttempts(postId = null) {
  const data = await apiJson(
    `/api/publish-attempts${buildQuery({ postId: postId || null })}`
  )
  return data.publishAttempts
}

export async function apiFetchRuntimeVerifications(
  limit = 30,
  checkKey = null,
  status
) {
  const data = await apiJson(
    `/api/runtime/verifications${buildQuery({
      limit,
      checkKey,
      status,
    })}`
  )
  return data.verifications
}

export async function apiRunRuntimeVerification(payload) {
  return apiJson('/api/runtime/verify', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function downloadFromUrl(downloadUrl, filename) {
  const a = document.createElement('a')
  a.href = `${PROXY}${downloadUrl}`
  a.download = filename
  a.click()
}

// --- Autonomy -------------------------------------------------------------

export async function apiFetchAutonomyState() {
  return apiJson('/api/autonomy/state')
}

export async function apiPauseAutonomy(reason = '') {
  return apiJson('/api/autonomy/pause', {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export async function apiResumeAutonomy() {
  return apiJson('/api/autonomy/resume', {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function apiUpdateAutonomyPolicy(contentType, updates) {
  return apiJson(`/api/autonomy/policies/${encodeURIComponent(contentType)}`, {
    method: 'POST',
    body: JSON.stringify(updates),
  })
}

export async function apiListBiasOverrides({
  activeOnly = false,
  pillar,
} = {}) {
  const params = {}
  if (activeOnly) params.activeOnly = 'true'
  if (pillar) params.pillar = pillar
  return apiJson(`/api/autonomy/bias-overrides${buildQuery(params)}`)
}

export async function apiCreateBiasOverride(payload) {
  return apiJson('/api/autonomy/bias-overrides', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiRevokeBiasOverride(id, { reason } = {}) {
  return apiJson(`/api/autonomy/bias-overrides/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason: reason || null }),
  })
}

export async function apiExtendBiasOverride(
  id,
  { extendDays = 7, reason } = {}
) {
  return apiJson(
    `/api/autonomy/bias-overrides/${encodeURIComponent(id)}/extend`,
    {
      method: 'POST',
      body: JSON.stringify({ extendDays, reason: reason || null }),
    }
  )
}

export async function apiListAutonomyDecisions({ limit = 50 } = {}) {
  return apiJson(
    `/api/autonomy/decisions${buildQuery({ limit: String(limit) })}`
  )
}

// Velocity tasks — post-publish operator prompts that fire at the
// platform-specific velocity windows (X: 90s/5m/15m/1h, LinkedIn:
// 5m/30m/90m/4h). 503 with VELOCITY_TASKS_UNAVAILABLE when the repo
// isn't wired. Per-post listing is via the postId query param.
export async function apiListVelocityTasksForPost(postId, { limit = 50 } = {}) {
  return apiJson(
    `/api/velocity-tasks${buildQuery({ postId, limit: String(limit) })}`
  )
}

export async function apiCompleteVelocityTask(id, action = {}) {
  return apiJson(`/api/velocity-tasks/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
    body: JSON.stringify(action || {}),
  })
}

export async function apiSkipVelocityTask(id, { reason } = {}) {
  return apiJson(`/api/velocity-tasks/${encodeURIComponent(id)}/skip`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || null }),
  })
}

// Per-post decision history. Returns 503 with code
// AUTONOMY_DECISION_AUDIT_UNAVAILABLE when the audit repo isn't wired.
// Callers should treat that case as "no decision history available"
// rather than a hard error — the post may still have a latest-decision
// snapshot in post_autonomy_state.
export async function apiListAutonomyDecisionsForPost(
  postId,
  { limit = 50 } = {}
) {
  return apiJson(
    `/api/autonomy/decisions/${encodeURIComponent(postId)}${buildQuery({
      limit: String(limit),
    })}`
  )
}

export async function apiAutoPromoteTopics(payload = {}) {
  return apiJson('/api/autonomy/topics/auto-promote', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Wave 25 — operator-triggered topic discovery + scheduler status.
export async function apiAutoDiscoverTopics(payload = {}) {
  return apiJson('/api/autonomy/topics/auto-discover', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiSchedulerStatus() {
  return apiJson('/api/autonomy/scheduler-status')
}

// Wave 29 — cadence policies + verdict.
export async function apiListCadencePolicies() {
  return apiJson('/api/autonomy/cadence-policies')
}

export async function apiUpsertCadencePolicy(platform, payload) {
  return apiJson(
    `/api/autonomy/cadence-policies/${encodeURIComponent(platform)}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    }
  )
}

export async function apiCadenceVerdict(platform) {
  return apiJson(
    `/api/autonomy/cadence-verdict?platform=${encodeURIComponent(platform)}`
  )
}

// Wave 30 — brand-safety policies, evaluate, overrides.
export async function apiListBrandSafetyPolicies({
  category,
  enabledOnly,
} = {}) {
  const params = {}
  if (category) params.category = category
  if (enabledOnly) params.enabledOnly = 'true'
  return apiJson(`/api/autonomy/brand-safety/policies${buildQuery(params)}`)
}

export async function apiCreateBrandSafetyPolicy(payload) {
  return apiJson('/api/autonomy/brand-safety/policies', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiUpdateBrandSafetyPolicy(id, payload) {
  return apiJson(
    `/api/autonomy/brand-safety/policies/${encodeURIComponent(id)}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    }
  )
}

export async function apiDeleteBrandSafetyPolicy(id) {
  return apiJson(
    `/api/autonomy/brand-safety/policies/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
    }
  )
}

export async function apiEvaluateBrandSafety({ postId, text }) {
  return apiJson('/api/autonomy/brand-safety/evaluate', {
    method: 'POST',
    body: JSON.stringify({ postId, text }),
  })
}

export async function apiCreateBrandSafetyOverride({
  postId,
  approvalNote,
  findingsAtApproval,
}) {
  return apiJson('/api/autonomy/brand-safety/overrides', {
    method: 'POST',
    body: JSON.stringify({ postId, approvalNote, findingsAtApproval }),
  })
}

export async function apiGetBrandSafetyOverride(postId) {
  return apiJson(
    `/api/autonomy/brand-safety/overrides/${encodeURIComponent(postId)}`
  )
}

// Wave 32 — voice corpus continuous calibration. Operator-triggered
// scan returns exemplary post candidates + drift signal.
export async function apiVoiceCorpusScan(payload = {}) {
  return apiJson('/api/autonomy/voice-corpus/scan', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Wave 22 — cross-channel format adaptation. Operator-triggered:
// take a canonical source post, generate variants for the target
// content types, persist them as derivative drafts.
export async function apiDerivePostVariants(postId, targetTypes = []) {
  return apiJson(`/api/posts/${encodeURIComponent(postId)}/derive`, {
    method: 'POST',
    body: JSON.stringify({ targetTypes }),
  })
}

export async function apiCalibrateVoice(payload = {}) {
  return apiJson('/api/autonomy/calibrate', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Wave 38 — notification center / activity feed.
//
// `unreadCount` from the response drives the bell badge; `events` drives
// the drawer. The bell pings on a 30s interval (or on visibility change)
// to surface new events without forcing an explicit refresh.

export async function apiListEvents({
  unreadOnly = false,
  kinds = null,
  limit = 50,
} = {}) {
  const params = new URLSearchParams()
  if (unreadOnly) params.set('unreadOnly', 'true')
  if (kinds && kinds.length > 0) params.set('kinds', kinds.join(','))
  if (limit) params.set('limit', String(limit))
  const qs = params.toString()
  return apiJson(`/api/events${qs ? `?${qs}` : ''}`)
}

export async function apiMarkEventRead(id) {
  return apiJson(`/api/events/${encodeURIComponent(id)}/read`, {
    method: 'POST',
  })
}

export async function apiMarkAllEventsRead() {
  return apiJson('/api/events/read-all', { method: 'POST' })
}

export async function apiCalibrateVoiceCorpus(payload = {}) {
  return apiJson('/api/autonomy/calibrate-corpus', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiListVoiceReferenceSamples({
  includeArchived = false,
  eraLabel = null,
} = {}) {
  const query = new URLSearchParams()
  if (includeArchived) query.set('includeArchived', 'true')
  if (eraLabel) query.set('eraLabel', eraLabel)
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return apiJson(`/api/voice/reference-samples${suffix}`)
}

export async function apiCreateVoiceReferenceSample(payload) {
  return apiJson('/api/voice/reference-samples', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiArchiveVoiceReferenceSample(id) {
  return apiJson(
    `/api/voice/reference-samples/${encodeURIComponent(id)}/archive`,
    { method: 'POST', body: JSON.stringify({}) }
  )
}

export async function apiRestoreVoiceReferenceSample(id) {
  return apiJson(
    `/api/voice/reference-samples/${encodeURIComponent(id)}/restore`,
    { method: 'POST', body: JSON.stringify({}) }
  )
}

export async function apiDeleteVoiceReferenceSample(id) {
  return apiJson(`/api/voice/reference-samples/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function apiListVoiceCalibrationDrifts(limit = 20) {
  return apiJson(`/api/voice/calibration-drifts?limit=${limit}`)
}

export async function apiResolveAutonomyIncident(id) {
  return apiJson(`/api/autonomy/incidents/${encodeURIComponent(id)}/resolve`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function apiEvaluateVoice({
  contentType,
  content,
  persist = false,
}) {
  return apiJson('/api/voice/evaluate', {
    method: 'POST',
    body: JSON.stringify({ contentType, content, persist }),
  })
}

export async function apiEvaluateGrounding({
  content,
  postId = null,
  persist = false,
}) {
  return apiJson('/api/grounding/evaluate', {
    method: 'POST',
    body: JSON.stringify({ content, postId, persist }),
  })
}

export async function apiEngagementSummary({ windowMs } = {}) {
  const params = {}
  if (windowMs) params.windowMs = windowMs
  return apiJson(`/api/engagement/summary${buildQuery(params)}`)
}

export async function apiEngagementSnapshot(payload = {}) {
  return apiJson('/api/engagement/snapshot', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiEngagementForPost(postId, { limit } = {}) {
  const params = {}
  if (limit) params.limit = limit
  return apiJson(
    `/api/engagement/posts/${encodeURIComponent(postId)}${buildQuery(params)}`
  )
}

export async function apiEngagementInsights({ since } = {}) {
  const params = {}
  if (since) params.since = since
  return apiJson(`/api/engagement/insights${buildQuery(params)}`)
}

// P1 tech-debt item 4 — follower growth tracking.
export async function apiGrowthFollowers({ provider = 'x', limit = 30 } = {}) {
  const params = {}
  if (provider) params.provider = provider
  if (limit) params.limit = limit
  return apiJson(`/api/growth/followers${buildQuery(params)}`)
}

// Wave 23b — pillar registry + lifecycle.
export async function apiPillars({ status } = {}) {
  const params = {}
  if (status) params.status = status
  return apiJson(`/api/pillars${buildQuery(params)}`)
}

export async function apiPillarLifecycle({
  threshold,
  minSampleSize,
  lookbackDays,
} = {}) {
  const params = {}
  if (threshold != null) params.threshold = threshold
  if (minSampleSize != null) params.minSampleSize = minSampleSize
  if (lookbackDays != null) params.lookbackDays = lookbackDays
  return apiJson(`/api/pillars/lifecycle${buildQuery(params)}`)
}

export async function apiDeprecatePillar(id, reason) {
  return apiJson(`/api/pillars/${encodeURIComponent(id)}/deprecate`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export async function apiReactivatePillar(id) {
  return apiJson(`/api/pillars/${encodeURIComponent(id)}/reactivate`, {
    method: 'POST',
  })
}

export async function apiAcceptPillar(id) {
  return apiJson(`/api/pillars/${encodeURIComponent(id)}/accept`, {
    method: 'POST',
  })
}

// ── Lancer (Wave A) ────────────────────────────────────────────────
// Conversational agent surface. See docs/lancer-vision.md.

export async function apiLancerProjects({ includeArchived = false } = {}) {
  const params = {}
  if (includeArchived) params.includeArchived = 'true'
  return apiJson(`/api/lancer/projects${buildQuery(params)}`)
}

export async function apiCreateLancerProject(payload) {
  return apiJson('/api/lancer/projects', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

export async function apiUpdateLancerProject(id, updates) {
  return apiJson(`/api/lancer/projects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(updates || {}),
  })
}

export async function apiArchiveLancerProject(id) {
  return apiJson(`/api/lancer/projects/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function apiLancerConversations({
  projectId,
  includeArchived = false,
} = {}) {
  const params = {}
  if (projectId !== undefined) params.projectId = projectId || ''
  if (includeArchived) params.includeArchived = 'true'
  return apiJson(`/api/lancer/conversations${buildQuery(params)}`)
}

export async function apiCreateLancerConversation(payload) {
  return apiJson('/api/lancer/conversations', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

export async function apiUpdateLancerConversation(id, updates) {
  return apiJson(`/api/lancer/conversations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(updates || {}),
  })
}

export async function apiArchiveLancerConversation(id) {
  return apiJson(`/api/lancer/conversations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function apiLancerMessages(conversationId, { limit } = {}) {
  const params = {}
  if (limit) params.limit = limit
  return apiJson(
    `/api/lancer/conversations/${encodeURIComponent(conversationId)}/messages${buildQuery(params)}`
  )
}

export async function apiSendLancerMessage(conversationId, payload) {
  return apiJson(
    `/api/lancer/conversations/${encodeURIComponent(conversationId)}/messages`,
    {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    }
  )
}

/**
 * Stream a Lancer message via SSE. Calls `onEvent({ kind, ... })` for
 * every server event. Returns a promise that resolves when the stream
 * ends (`done` event) or rejects if the network drops.
 *
 * @param {string} conversationId
 * @param {object} payload         { text, provider, model, currentView }
 * @param {(evt: object) => void} onEvent
 * @param {{ signal?: AbortSignal }} [opts]
 */
export async function apiSendLancerMessageStream(
  conversationId,
  payload,
  onEvent,
  { signal } = {}
) {
  const token = await ensureCsrfToken()
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  }
  if (token) headers[CSRF_HEADER] = token

  const res = await fetch(
    `${PROXY}/api/lancer/conversations/${encodeURIComponent(conversationId)}/messages/stream`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(payload || {}),
      signal,
    }
  )
  captureCsrfToken(res.headers)
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    const err = new Error(`Stream request failed: ${res.status}`)
    err.status = res.status
    err.code = 'STREAM_REQUEST_FAILED'
    err.body = text
    throw err
  }

  // SSE parser. Anthropic / our route emit `data: <json>\n\n` events
  // (and the route also writes `: keep-alive\n\n` heartbeats which
  // start with `:` — those parse to empty data and are ignored).
  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buf = ''
  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    let idx
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const raw = buf.slice(0, idx)
      buf = buf.slice(idx + 2)
      let data = ''
      for (const line of raw.split('\n')) {
        if (line.startsWith('data: ')) data += line.slice(6)
      }
      if (!data) continue
      try {
        const parsed = JSON.parse(data)
        onEvent(parsed)
        if (parsed.kind === 'done') return
      } catch {
        // Drop malformed events silently — heartbeats live here too.
      }
    }
  }
}

export async function apiLancerActivations({ projectId, status } = {}) {
  const params = {}
  if (projectId) params.projectId = projectId
  if (status) params.status = status
  return apiJson(`/api/lancer/activations${buildQuery(params)}`)
}

export async function apiCreateLancerActivation(payload) {
  return apiJson('/api/lancer/activations', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

export async function apiEndLancerActivation(id, payload = {}) {
  return apiJson(`/api/lancer/activations/${encodeURIComponent(id)}/end`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function apiLancerBypasses({ scope, scopeId } = {}) {
  const params = {}
  if (scope) params.scope = scope
  if (scopeId) params.scopeId = scopeId
  return apiJson(`/api/lancer/bypasses${buildQuery(params)}`)
}

export async function apiGrantLancerBypass(payload) {
  return apiJson('/api/lancer/bypasses', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

export async function apiRevokeLancerBypass(id) {
  return apiJson(`/api/lancer/bypasses/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function apiLancerModels() {
  return apiJson('/api/lancer/models')
}

// Wave B — pending action approvals.

export async function apiLancerPendingActions(conversationId, { status } = {}) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : ''
  return apiJson(
    `/api/lancer/conversations/${encodeURIComponent(conversationId)}/pending-actions${qs}`
  )
}

export async function apiApproveLancerAction(
  conversationId,
  actionId,
  { input = null, rememberScope = null } = {}
) {
  return apiJson(
    `/api/lancer/conversations/${encodeURIComponent(conversationId)}/actions/${encodeURIComponent(actionId)}/approve`,
    {
      method: 'POST',
      body: JSON.stringify({ input, rememberScope }),
    }
  )
}

export async function apiRejectLancerAction(
  conversationId,
  actionId,
  { reason = '' } = {}
) {
  return apiJson(
    `/api/lancer/conversations/${encodeURIComponent(conversationId)}/actions/${encodeURIComponent(actionId)}/reject`,
    {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }
  )
}

export async function apiResumeLancerConversation(
  conversationId,
  { provider, model, currentView } = {}
) {
  return apiJson(
    `/api/lancer/conversations/${encodeURIComponent(conversationId)}/messages/resume`,
    {
      method: 'POST',
      body: JSON.stringify({ provider, model, currentView }),
    }
  )
}

// Wave B.3 — operator-triggerable activation tick. Runs ONE round of
// the activation loop server-side. Returns the summary describing
// what happened (tick_complete, awaiting_approval, kill_switch,
// time_expired, etc).
export async function apiTickLancerActivation(activationId) {
  return apiJson(
    `/api/lancer/activations/${encodeURIComponent(activationId)}/tick`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    }
  )
}

// Wave C — open widget framework. Operator-saved widgets backed by
// a JSON descriptor (kind / title / source tool / config). The
// renderer fetches fresh data via the /data endpoint, which proxies
// to the widget's source tool with read-only access.

export async function apiLancerWidgets({
  projectId,
  includeGlobal = true,
  includeArchived = false,
} = {}) {
  const params = {}
  if (projectId) params.projectId = projectId
  if (includeGlobal != null) params.includeGlobal = String(includeGlobal)
  if (includeArchived) params.includeArchived = 'true'
  return apiJson(`/api/lancer/widgets${buildQuery(params)}`)
}

export async function apiCreateLancerWidget({
  name,
  projectId = null,
  descriptor,
  pinned = false,
}) {
  return apiJson('/api/lancer/widgets', {
    method: 'POST',
    body: JSON.stringify({ name, projectId, descriptor, pinned }),
  })
}

export async function apiUpdateLancerWidget(id, patch) {
  return apiJson(`/api/lancer/widgets/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch || {}),
  })
}

export async function apiArchiveLancerWidget(id) {
  return apiJson(`/api/lancer/widgets/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

// Wave C polish — duplicate a widget to a different scope. The
// original stays in place. Pass targetProjectId=null for global,
// or a project id to scope the copy to that project.
export async function apiDuplicateLancerWidget(
  id,
  { targetProjectId = null, name } = {}
) {
  return apiJson(`/api/lancer/widgets/${encodeURIComponent(id)}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({ targetProjectId, name }),
  })
}

// Wave C polish — restore an archived widget. Symmetric with
// apiArchiveLancerWidget (DELETE). Use POST .../restore so we don't
// overload PATCH with archive-state semantics.
export async function apiRestoreLancerWidget(id) {
  return apiJson(`/api/lancer/widgets/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

// Wave D — signal engine. The signals repo is fed by the watcher
// in the autonomy worker; the operator can also trigger a poll
// out-of-band via /poll-now.

export async function apiLancerSignals({
  source,
  sourceKind,
  projectId,
  since,
  minScore,
  limit,
} = {}) {
  const params = {}
  if (source) params.source = source
  if (sourceKind) params.sourceKind = sourceKind
  if (projectId) params.projectId = projectId
  if (since) params.since = since
  if (minScore != null) params.minScore = String(minScore)
  if (limit != null) params.limit = String(limit)
  return apiJson(`/api/lancer/signals${buildQuery(params)}`)
}

export async function apiPollLancerSignals({ sourceRef = null } = {}) {
  return apiJson('/api/lancer/signals/poll-now', {
    method: 'POST',
    body: JSON.stringify({ sourceRef }),
  })
}

export async function apiUpdateLancerSignal(
  id,
  { manualScore, projectId } = {}
) {
  const body = {}
  if (manualScore !== undefined) body.manualScore = manualScore
  if (projectId !== undefined) body.projectId = projectId
  return apiJson(`/api/lancer/signals/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

// Wave D.3 — manually trigger a scoring batch. Useful after tuning
// audience keywords / weights to re-score the ledger without
// waiting for the scheduler.
export async function apiScoreLancerSignals({ limit = 50 } = {}) {
  return apiJson('/api/lancer/signals/score-now', {
    method: 'POST',
    body: JSON.stringify({ limit }),
  })
}

// Wave E.2 — standing rules. Operator types NL, server parses via
// Claude into structured form, persists. Engine runs on a 10-minute
// cycle in the autonomy worker (gated by STUDIO_LANCER_RULE_ENGINE).

export async function apiLancerRules({
  scope,
  projectId,
  enabled,
  includeArchived,
} = {}) {
  const params = {}
  if (scope) params.scope = scope
  if (projectId) params.projectId = projectId
  if (enabled !== undefined) params.enabled = String(enabled)
  if (includeArchived) params.includeArchived = 'true'
  return apiJson(`/api/lancer/rules${buildQuery(params)}`)
}

export async function apiCreateLancerRule({
  naturalLanguage,
  defaultProjectId,
}) {
  return apiJson('/api/lancer/rules', {
    method: 'POST',
    body: JSON.stringify({ naturalLanguage, defaultProjectId }),
  })
}

export async function apiUpdateLancerRule(id, patch) {
  return apiJson(`/api/lancer/rules/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch || {}),
  })
}

export async function apiArchiveLancerRule(id) {
  return apiJson(`/api/lancer/rules/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function apiRestoreLancerRule(id) {
  return apiJson(`/api/lancer/rules/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function apiLancerRuleFirings(id, { limit = 50 } = {}) {
  return apiJson(
    `/api/lancer/rules/${encodeURIComponent(id)}/firings?limit=${limit}`
  )
}

// Operator-triggered rule engine cycle. Useful after creating a new
// rule for testing — bypasses the 10min cycle.
export async function apiRunLancerRules({ limit = 100 } = {}) {
  return apiJson('/api/lancer/rules/run-now', {
    method: 'POST',
    body: JSON.stringify({ limit }),
  })
}

// Wave E.5 — operator-triggered reflection. Reads the last sinceMs
// of activity (default 7 days) and writes a 'lancer-reflection'
// studio event the operator sees in the dashboard.
export async function apiReflectLancer({ sinceMs } = {}) {
  return apiJson('/api/lancer/reflect-now', {
    method: 'POST',
    body: JSON.stringify(sinceMs ? { sinceMs } : {}),
  })
}

// Wave F.3 — operator-triggered conversation cycle. Reads recent
// inbound social signals and queues drafts as pending actions for
// approval. Returns { summary: { considered, drafted, skipped, errors } }.
export async function apiRunLancerConversation({ minScore, maxPerCycle } = {}) {
  const body = {}
  if (Number.isFinite(minScore)) body.minScore = minScore
  if (Number.isFinite(maxPerCycle)) body.maxPerCycle = maxPerCycle
  return apiJson('/api/lancer/conversation-now', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

// ── Wave G.2 — engagement audit dashboard ────────────────────────

export async function apiLancerEngagementSummary() {
  return apiJson('/api/lancer/engagement/summary')
}

// Engagement action queue (2026-06-04) — one fetch for the Engagement
// tab: { toSend, inbound, autonomous, creatorPosts, counts }. Powers the
// daily loop + the app-wide nav badge.
export async function apiLancerEngagementQueue() {
  return apiJson('/api/lancer/engagement/queue')
}

// X profile (followers etc.) for the Engagement metrics strip. Live X
// call, so the tab fetches it once on mount (not on the badge poll).
export async function apiTwitterProfile() {
  return apiJson('/api/twitter/me')
}

// On-demand comment draft for a curated-creator post. Generates a
// voice-gated reply on the spot and queues it as a pending manual-send
// action. Returns { draft, reused }. A 422 means the draft was rejected
// (voice floor / brand safety) — the caller surfaces the reason.
export async function apiDraftCommentForPost(signalId) {
  return apiJson('/api/lancer/engagement/draft-comment', {
    method: 'POST',
    body: JSON.stringify({ signalId }),
  })
}

// ── Wave G.3 — auto-draft approval queue ─────────────────────────

export async function apiLancerAutoDrafts() {
  return apiJson('/api/lancer/auto-drafts')
}

export async function apiApproveLancerAutoDraft(id, { editedText } = {}) {
  return apiJson(`/api/lancer/auto-drafts/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: JSON.stringify(editedText ? { editedText } : {}),
  })
}

export async function apiRejectLancerAutoDraft(id, { reason } = {}) {
  return apiJson(`/api/lancer/auto-drafts/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  })
}

// ── Wave E.1 — multi-source registry ──────────────────────────────

export async function apiLancerSources({
  kind = null,
  projectId = null,
  includeArchived = false,
} = {}) {
  const params = new URLSearchParams()
  if (kind) params.set('kind', kind)
  if (projectId) params.set('projectId', projectId)
  if (includeArchived) params.set('includeArchived', 'true')
  const qs = params.toString()
  return apiJson(`/api/lancer/sources${qs ? `?${qs}` : ''}`)
}

export async function apiCreateLancerSource({
  kind,
  ref,
  displayName = null,
  projectId = null,
  config = null,
}) {
  return apiJson('/api/lancer/sources', {
    method: 'POST',
    body: JSON.stringify({
      kind,
      ref,
      displayName,
      projectId,
      config,
    }),
  })
}

export async function apiUpdateLancerSource(id, updates) {
  return apiJson(`/api/lancer/sources/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
}

export async function apiArchiveLancerSource(id) {
  return apiJson(`/api/lancer/sources/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function apiRestoreLancerSource(id) {
  return apiJson(`/api/lancer/sources/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function apiPollLancerSources() {
  return apiJson('/api/lancer/sources/poll-now', {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function apiLancerWidgetData(id) {
  return apiJson(`/api/lancer/widgets/${encodeURIComponent(id)}/data`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

// Wave C polish — render a descriptor without persisting. Used by the
// confirmation card to show the operator what the proposed widget
// will actually display, with real data, before they click Approve.
export async function apiLancerWidgetPreview(descriptor) {
  return apiJson('/api/lancer/widgets/preview', {
    method: 'POST',
    body: JSON.stringify({ descriptor }),
  })
}

// Wave C polish — server-side dock layout. Replace localStorage with
// per-scope persistence so the operator's dock syncs across machines.
// `getForScope` always returns a layout shape (defaults to empty
// arrays when no row exists yet) so callers don't have to special-case.
export async function apiLancerDockLayout({ projectId } = {}) {
  const params = projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''
  return apiJson(`/api/lancer/dock-layout${params}`)
}

export async function apiSaveLancerDockLayout({
  projectId = null,
  starterIds,
  customIds,
}) {
  return apiJson('/api/lancer/dock-layout', {
    method: 'PUT',
    body: JSON.stringify({ projectId, starterIds, customIds }),
  })
}
